import CustomCarousel from '@ui/custom-carousel/custom-carousel';
import { BreakoutRoom } from '@/types/session.types';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import { Dispatch, SetStateAction, useCallback, useRef } from 'react';
import getDefaultBreakoutRoom from './helpers/get-default-breakout-room';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import { useParams } from 'react-router-dom';
import { updateTranslateKey } from '@/utils/update-translation-key';
import { GetDefaultTranslateString } from '@/utils/create-event';
import Text from '@ui/text/text/text';
import OptionalComponent from '@ui/optional-component/optional-component';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

type TGoogleMeetBreakoutsProps = {
  breakoutRooms: BreakoutRoom[];
  setBreakoutRooms: Dispatch<SetStateAction<BreakoutRoom[]>>;
  defaultLanguage?: string;
};

const GoogleMeetBreakouts = ({ breakoutRooms, setBreakoutRooms, defaultLanguage }: TGoogleMeetBreakoutsProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const params = useParams();
  const language = params.language || defaultLanguage || 'en';

  const scrollToEndTimer = useRef<NodeJS.Timeout | null>(null);
  const focusInputTimer = useRef<NodeJS.Timeout | null>(null);

  const handleAddNewRoom = useCallback(() => {
    setBreakoutRooms((prev) => [...prev, getDefaultBreakoutRoom()]);

    if (scrollToEndTimer.current) {
      clearTimeout(scrollToEndTimer.current);
    }
    scrollToEndTimer.current = setTimeout(() => {
      if (scrollRef?.current) {
        scrollRef.current.scrollTo({
          left: scrollRef.current.scrollWidth,
          behavior: 'smooth',
        });

        if (focusInputTimer.current) {
          clearTimeout(focusInputTimer.current);
        }
        focusInputTimer.current = setTimeout(() => {
          // get last .create-breakout-room-name-input from inside scrollRef and focus it
          const allInputs = scrollRef?.current?.querySelectorAll('.create-breakout-room-name-input');
          const lastInput = allInputs?.[allInputs.length - 1] as HTMLInputElement | null;
          if (lastInput) {
            lastInput.focus();
          }
        }, 600);
      }
    }, 0);
  }, [setBreakoutRooms, scrollRef]);

  const handleRemoveRoom = useCallback(
    (uuid: string) => {
      setBreakoutRooms((prev) => prev.filter((room) => room.uuid !== uuid));
    },
    [setBreakoutRooms]
  );

  const handleInputChange = useCallback(
    ({ key, item }: { key: 'name' | 'description'; item: BreakoutRoom }) =>
      (_value: string) => {
        const value = _value.trim();
        setBreakoutRooms((prevRooms) =>
          prevRooms.map((room) => {
            if (room.uuid !== item.uuid) return room;
            let translateString = GetDefaultTranslateString();
            if (key === 'name' && room.name_translations) {
              translateString = room.name_translations;
            }
            if (key === 'description' && room.description_translations) {
              translateString = room.description_translations;
            }
            const translationsKey = key === 'name' ? 'name_translations' : 'description_translations';
            return {
              ...room,
              ...(language === defaultLanguage ? { [key]: value } : {}),
              [translationsKey]: updateTranslateKey({
                translateString,
                input: value,
                baseLanguage: defaultLanguage || language,
                language,
              }),
            };
          })
        );
      },
    [setBreakoutRooms, language, defaultLanguage]
  );

  return (
    <Container width="100%" height="100%" className="flex-column">
      <div className="flex ai-center jc-flex-end margin-v-12">
        <Button
          width="fit-content margin-r-0"
          onClick={handleAddNewRoom}
          clear
          className="flex ai-center jc-center margin-r-8 radius-100 padding-h-12"
          hoverOpacity={0.8}
        >
          <Text color={getThemeColorCSSVariable()} className="S3 text-center flex ai-center gap-4">
            <Icon name={ICONS.ADD} size={12} /> <span>Add room</span>
          </Text>
        </Button>
      </div>

      <Container className="flex ai-center" height="240px">
        <CustomCarousel
          allowDragOnClickableItems={false}
          scrollRef={scrollRef}
          list={breakoutRooms.map((room) => ({
            ...room,
            id: room.id || room.uuid,
          }))}
          gap={'12px'}
          ignorePointerEvents={false}
          renderItem={(item, idx) => (
            <Container
              background="var(--gray-900)"
              className="flex-col gap-2"
              height="240px"
              width="576px"
              borderRadius="12px"
              padding="16px"
            >
              <div className="flex ai-center jc-space-between margin-b-8">
                <Text color={'var(--gray-300'} className="S2">
                  Breakout room {idx + 1}
                </Text>
                <OptionalComponent display={breakoutRooms.length > 1}>
                  <Button onClick={() => handleRemoveRoom(item.uuid)} clear>
                    <Icon name={ICONS.TRASH} color="var(--semantic-red-500)" />
                  </Button>
                </OptionalComponent>
              </div>
              <InputWithOverlay
                label="Room Name *"
                inputProps={{
                  className: 'create-breakout-room-name-input',
                  defaultValue: item.name_translations?.[language] || item.name || '',
                  onBlur: (e) => {
                    handleInputChange({
                      key: 'name',
                      item: {
                        ...item,
                        // id number just to satisfy type, we don't actually use it for anything in the create flow
                        id: typeof item.id === 'number' ? item.id : Math.random(),
                      },
                    })(e.target.value);
                  },
                }}
                onDebounce={(value) => {
                  handleInputChange({
                    key: 'name',
                    item: {
                      ...item,
                      // id number just to satisfy type, we don't actually use it for anything in the create flow
                      id: typeof item.id === 'number' ? item.id : Math.random(),
                    },
                  })(value);
                }}
                dirtyError={({ dirty, setHasError }) => {
                  const hasError = dirty && !item.name.trim().length;
                  setHasError(hasError);
                }}
                debounceTime={700}
              />
              <InputWithOverlay
                label="Description"
                inputProps={{
                  defaultValue: item.description_translations?.[language] || item.description || '',
                  onBlur: (e) => {
                    handleInputChange({
                      key: 'description',
                      item: {
                        ...item,
                        // id number just to satisfy type, we don't actually use it for anything in the create flow
                        id: typeof item.id === 'number' ? item.id : Math.random(),
                      },
                    })(e.target.value);
                  },
                }}
                onDebounce={(value) => {
                  handleInputChange({
                    key: 'description',
                    item: {
                      ...item,
                      // id number just to satisfy type, we don't actually use it for anything in the create flow
                      id: typeof item.id === 'number' ? item.id : Math.random(),
                    },
                  })(value);
                }}
                debounceTime={700}
              />
            </Container>
          )}
        />
      </Container>
    </Container>
  );
};

export default GoogleMeetBreakouts;
